import {
	Avatar,
	Checkbox,
	Chip,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
	useTheme,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import stylesLogo from '../Accounts/Account.styles';
import { capitalize } from '../../hepler/functions';
import { handleActivationKey } from '../../utils/a11y.utils';
import sizeFontCustom from './fontSize.styles';
import { useTranslation } from '../../hooks/useTranslation';
import { getFipLogoAlt } from '../../utils/fipLogo.utils';
import { toast } from 'react-toastify';

function FipDiscoveredAccountsLegacy(props) {
	const { t } = useTranslation();
	const theme = useTheme();
	const {
		account,
		handleCheckedState,
		selectedAccounts,
		fipId,
		isLinked,
		isVerified,
		customDetails,
	} = props;

	const isDark =
		customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	const fipName = t(`fips.${global.objectFormatFip[fipId]?.fipId}`);

	const isMandatory =
		account?.restrictions?.isMandatory?.toLowerCase() === 'true';

	const showCheckbox = !(
		isLinked ||
		isVerified?.includes(account.accRefNumber) ||
		isVerified?.length > 0
	);

	const isChecked =
		selectedAccounts.some(
			(acc) => acc.accRefNumber === account.accRefNumber,
		) || false;

	const accountCheckboxLabel = `${capitalize(account.accType)} ${account.maskedAccNumber.replaceAll('X', '\u2022')}`;

	const handleRowClick = (event) => {
		if (!showCheckbox || event.target.closest('.MuiCheckbox-root')) {
			return;
		}

		if (isMandatory && isChecked) {
			event.preventDefault();
			toast.error(t('accountDiscovery.errors.unselectMandatoryAccounts'));
			return;
		}

		handleCheckedState(
			{
				...event,
				target: {
					checked: !isChecked,
					name: account.accRefNumber,
				},
			},
			account,
		);
	};

	const handleCheckboxChange = (event) => {
		if (isMandatory && !event.target.checked) {
			event.preventDefault();
			toast.error(t('accountDiscovery.errors.unselectMandatoryAccounts'));
			return;
		}
		handleCheckedState(event, account);
	};

	return (
		<ListItem
			component="li"
			onClick={handleRowClick}
			key={account.accRefNumber}
			sx={{
				ml: '0.75rem',
				maxWidth: '95%',
				cursor: showCheckbox ? 'pointer' : 'default',
				listStyle: 'none',
			}}
			disablePadding
		>
			<ListItemIcon sx={{ minWidth: 0, mr: '0.25rem' }}>
				{showCheckbox && (
					<Checkbox
						sx={{
							padding: 0,
							color: isDark ? '#7A8185' : '#D8E1EE',
							'&.Mui-checked': {
								color: customDetails?.primaryColor || '#3053D3',
							},
							'&.Mui-disabled': {
								color: isDark ? '#4A4A4A' : '#BDBDBD',
								'&.Mui-checked': {
									color: isDark ? '#4A4A4A' : '#BDBDBD',
								},
							},
						}}
						size="small"
						disabled={isMandatory}
						name={account.accRefNumber}
						checked={isChecked}
						onChange={handleCheckboxChange}
						onClick={(event) => event.stopPropagation()}
						inputProps={{ 'aria-label': accountCheckboxLabel }}
					/>
				)}
			</ListItemIcon>
			<ListItemText
				primary={
					<List disablePadding component="div">
						<ListItem
							component="div"
							sx={{
								m: '0.5rem 1.25rem 0 0',
								background: isDark ? '#3D3D3D' : theme.palette.primary.light,
								color: isDark ? '#FFFFFF' : '#000000',
								p: '0.5rem',
								borderRadius: '14px',
								border: 'none',
							}}
						>
							<ListItemIcon>
								<Avatar
									alt={getFipLogoAlt(fipId, fipName)}
									src={global.objectFormatFip[fipId]?.productIconUri}
									sx={{
										...stylesLogo.avatarStyle,
										background: isDark
											? '#2D2D2D'
											: theme.palette.primary.light,
									}}
								/>
							</ListItemIcon>
							<ListItemText
								sx={{
									'& .MuiListItemText-primary': {
										color: isDark ? '#FFFFFF' : theme.palette.info.main,
										...sizeFontCustom.subHeading,
									},
									'& .MuiListItemText-secondary': {
										color: isDark ? '#7A8185' : theme.palette.black60,
										fontSize: '0.75rem',
									},
									borderRadius: '14px',
									m: 0,
								}}
								primary={capitalize(account.accType)}
								secondary={account.maskedAccNumber.replaceAll('X', '\u2022')}
							/>
							{(isLinked || isVerified?.includes(account.accRefNumber)) && (
								<Chip
									variant="outlined"
									label={
										<Typography
											sx={{
												...sizeFontCustom.subHeading,
												color: isDark ? '#FFFFFF' : '#000000',
											}}
										>
											{t('common.buttons.linked')}
										</Typography>
									}
									sx={{
										backgroundColor: isDark
											? '#2D2D2D'
											: theme.palette.primary.white,
										borderColor: isDark ? '#7A8185' : 'inherit',
										color: isDark ? '#FFFFFF' : '#000000',
									}}
									size="small"
									color="success"
								/>
							)}
						</ListItem>
					</List>
				}
			/>
		</ListItem>
	);
}

function FipDiscoveredAccountsNew(props) {
	const { t } = useTranslation();
	const theme = useTheme();
	const {
		account,
		handleCheckedState,
		selectedAccounts,
		isLinked,
		isVerified,
		customDetails,
	} = props;

	const isDark =
		customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const secondaryTextColor =
		customDetails?.secondaryTextColor ?? theme.palette.text.secondary;
	const isLinkedOrVerified =
		isLinked || isVerified?.includes(account.accRefNumber);
	const isChecked = selectedAccounts.some(
		(acc) => acc.accRefNumber === account.accRefNumber,
	);

	const isMandatory =
		account?.restrictions?.isMandatory?.toLowerCase() === 'true';

	const accountLabel = `${capitalize(account.accType)} ${account.maskedAccNumber.slice(-4)}`;

	const handleClick = (event) => {
		if (isMandatory && isChecked) {
			event.preventDefault();
			toast.error(t('accountDiscovery.errors.unselectMandatoryAccounts'));
			return;
		}

		const syntheticEvent = {
			...event,
			target: {
				checked: !isChecked,
				name: account.accRefNumber,
			},
		};
		handleCheckedState(syntheticEvent, account);
	};

	return (
		<ListItem
			component="li"
			onClick={handleClick}
			onKeyDown={handleActivationKey(handleClick)}
			role="button"
			tabIndex={0}
			aria-label={accountLabel}
			disablePadding
			sx={{
				px: '0.5rem',
				py: '0.375rem',
				cursor: 'pointer',
				borderRadius: '8px',
				width: '100%',
				alignItems: 'center',
				listStyle: 'none',
				'&:hover': {
					backgroundColor: isDark
						? 'rgba(255,255,255,0.05)'
						: 'rgba(0,0,0,0.03)',
				},
			}}
		>
			<ListItemIcon sx={{ minWidth: 0, mr: '0.5rem' }}>
				<Checkbox
					sx={{
						padding: 0,
						color: isDark ? '#7A8185' : '#D8E1EE',
						'&.Mui-checked': {
							color: customDetails?.primaryColor || '#3053D3',
						},
						'&.Mui-disabled': {
							color: isDark ? '#4A4A4A' : '#BDBDBD',
							'&.Mui-checked': { color: isDark ? '#4A4A4A' : '#BDBDBD' },
						},
					}}
					size="small"
					name={account.accRefNumber}
					checked={isChecked}
					disabled={isMandatory}
					onChange={(event) => {
						if (isMandatory && !event.target.checked) {
							event.preventDefault();
							toast.error(
								t('accountDiscovery.errors.unselectMandatoryAccounts'),
							);
							return;
						}
						handleCheckedState(event, account);
					}}
					onClick={(event) => event.stopPropagation()}
					inputProps={{ 'aria-label': accountLabel }}
				/>
			</ListItemIcon>

			<ListItemText
				sx={{ m: 0, flex: 1 }}
				primary={
					<Typography
						sx={{
							...sizeFontCustom.subHeading,
							fontSize: '14px',
							color: secondaryTextColor,
							fontWeight: 400,
						}}
					>
						{capitalize(account.accType)} &nbsp;•&nbsp;{' '}
						{account.maskedAccNumber.slice(-4)}
					</Typography>
				}
			/>

			{isLinkedOrVerified && (
				<Chip
					icon={
						<LinkIcon
							sx={{
								fontSize: '14px !important',
								color: '#008809 !important',
								transform: 'rotate(-45deg)',
							}}
						/>
					}
					label={
						<Typography
							sx={{
								fontSize: '12px',
								lineHeight: 1,
								color: '#008809',
								fontWeight: 500,
							}}
						>
							{t('common.buttons.linked')}
						</Typography>
					}
					size="small"
					sx={{
						backgroundColor: isDark ? 'rgba(0,136,9,0.15)' : '#DEFFE0',
						border: 'none',
						borderRadius: '4px',
						padding: '2px 8px 2px 5px',
						gap: '2px',
						height: 'auto',
						flexShrink: 0,
						'& .MuiChip-icon': { ml: 0, mr: 0, p: 0 },
						'& .MuiChip-label': { px: 0, py: 0 },
					}}
				/>
			)}
		</ListItem>
	);
}

function FipDiscoveredAccounts(props) {
	if (props.useNewDiscoveryUI) {
		return <FipDiscoveredAccountsNew {...props} />;
	}
	return <FipDiscoveredAccountsLegacy {...props} />;
}

export default FipDiscoveredAccounts;
